import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../common/element/Input/Input";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";
import ConfirmationModal from "../../../common/Common/ConfirmationModal";
import ImagePreviewModal from "../../../common/ImagePreviewModal/ImagePreviewModal"; // <-- Import the new modal
import {
  getAdminProfile,
  updateAdminProfile,
  uploadProfileImage,
  deleteProfileImage,
  uploadCompanyLogo,
  deleteCompanyLogo,
  clearUpdateAdminProfile,
  clearUploadProfileImage,
  clearDeleteProfileImage,
  clearUploadCompanyLogo,
  clearDeleteCompanyLogo,
} from "../../../redux/Setting/Settingsslice";
import { selectUserData } from "../../../redux/auth/authSelector";
import { ROLES } from "../../../config/rolePermissions";
import {
  selectAdminProfileData,
  selectAdminProfileLoading,
  selectUpdateAdminProfileLoading,
  selectUpdateAdminProfileData,
  selectUpdateAdminProfileError,
  selectUploadProfileImageLoading,
  selectUploadProfileImageData,
  selectUploadProfileImageError,
  selectDeleteProfileImageLoading,
  selectDeleteProfileImageData,
  selectDeleteProfileImageError,
  selectUploadCompanyLogoLoading,
  selectUploadCompanyLogoData,
  selectUploadCompanyLogoError,
  selectDeleteCompanyLogoLoading,
  selectDeleteCompanyLogoData,
  selectDeleteCompanyLogoError,
} from "../../../redux/Setting/Settingsselectors";

const PersonalInfoForm = () => {
  const dispatch = useDispatch();

  // Profile fetch
  const profile = useSelector(selectAdminProfileData);
  const loading = useSelector(selectAdminProfileLoading);

  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;

  // Roles for which company name should be disabled and not sent in API
  const isCompanyDisabled = [
    ROLES.ADMIN,
    ROLES.EMPLOYEE,
    ROLES.SCIENCEOFSELF,
    ROLES.SALESTRAININGCOMPANY,
    ROLES.HR,
    ROLES.COACH,
  ].includes(userRole);

  // Roles that can see company logo section
  const canSeeCompanyLogo = [
    ROLES.COMPANYADMIN,
    ROLES.HR,
    ROLES.EMPLOYEE
  ].includes(userRole);

  // Only company admin can upload/delete company logo
  const canUploadCompanyLogo = userRole === ROLES.COMPANYADMIN;

  // Update profile
  const updateLoading = useSelector(selectUpdateAdminProfileLoading);
  const updateData = useSelector(selectUpdateAdminProfileData);
  const updateError = useSelector(selectUpdateAdminProfileError);

  // Upload profile image
  const uploadLoading = useSelector(selectUploadProfileImageLoading);
  const uploadData = useSelector(selectUploadProfileImageData);
  const uploadError = useSelector(selectUploadProfileImageError);

  // Delete profile image
  const deleteLoading = useSelector(selectDeleteProfileImageLoading);
  const deleteData = useSelector(selectDeleteProfileImageData);
  const deleteError = useSelector(selectDeleteProfileImageError);

  // Upload company logo
  const uploadCompanyLogoLoading = useSelector(selectUploadCompanyLogoLoading);
  const uploadCompanyLogoData = useSelector(selectUploadCompanyLogoData);
  const uploadCompanyLogoError = useSelector(selectUploadCompanyLogoError);

  // Delete company logo
  const deleteCompanyLogoLoading = useSelector(selectDeleteCompanyLogoLoading);
  const deleteCompanyLogoData = useSelector(selectDeleteCompanyLogoData);
  const deleteCompanyLogoError = useSelector(selectDeleteCompanyLogoError);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    photo: null,
    photoUrl: null,
    companyLogo: null,
    companyLogoUrl: null,
  });

  const fileInputRef = useRef(null);
  const companyLogoInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [companyLogoError, setCompanyLogoError] = useState(false);

  // Image Preview Modal State
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    url: "",
    alt: "",
  });

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null,
  });

  // Sanitize URL — remove double slashes (except after protocol)
  const sanitizeImageUrl = (url) => {
    if (!url) return null;
    return url.replace(/([^:]\/)\/+/g, "$1");
  };

  // ---------- MOUNT ----------
  useEffect(() => {
    dispatch(getAdminProfile());
    dispatch(clearUpdateAdminProfile());
    dispatch(clearUploadProfileImage());
    dispatch(clearDeleteProfileImage());
    dispatch(clearUploadCompanyLogo());
    dispatch(clearDeleteCompanyLogo());
    // eslint-disable-next-line
  }, [dispatch]);

  // ---------- POPULATE FORM ----------
  useEffect(() => {
    if (profile) {
      const imageUrl = sanitizeImageUrl(profile.profile_image);
      const logoUrl = sanitizeImageUrl(profile.company_logo);
      setFormData({
        firstName: profile.first_name || "",
        lastName: profile.last_name || "",
        email: profile.email || "",
        phone: profile.mobile_number || "",
        companyName: profile.company_name || "",
        photo: null,
        photoUrl: imageUrl,
        companyLogo: null,
        companyLogoUrl: logoUrl,
      });
      setPhotoPreview(imageUrl);
      setCompanyLogoPreview(logoUrl);
      setImageError(false);
      setCompanyLogoError(false);
    }
    // eslint-disable-next-line
  }, [profile]);

  // ---------- SUCCESS / ERROR WATCHERS ----------
  // Profile update success
  useEffect(() => {
    if (updateData) {
      SweetAlertService.showSuccess({
        title: "Updated!",
        message: "Profile updated successfully.",
      });
      dispatch(getAdminProfile());
      dispatch(clearUpdateAdminProfile());
    }
  }, [updateData, dispatch]);

  // Profile update error
  useEffect(() => {
    if (updateError) {
      SweetAlertService.showError({
        title: "Update Failed",
        message: updateError?.message || updateError || "Failed to update profile.",
      });
      dispatch(clearUpdateAdminProfile());
    }
  }, [updateError, dispatch]);

  // Profile image upload success
  useEffect(() => {
    if (uploadData) {
      SweetAlertService.showSuccess({
        title: "Uploaded!",
        message: "Profile image uploaded successfully.",
      });
      dispatch(getAdminProfile());
      dispatch(clearUploadProfileImage());
    }
  }, [uploadData, dispatch]);

  // Profile image upload error
  useEffect(() => {
    if (uploadError) {
      SweetAlertService.showError({
        title: "Upload Failed",
        message: uploadError?.message || uploadError || "Failed to upload image.",
      });
      setPhotoPreview(formData.photoUrl);
      dispatch(clearUploadProfileImage());
    }
    // eslint-disable-next-line
  }, [uploadError, dispatch]);

  // Profile image delete success
  useEffect(() => {
    if (deleteData) {
      SweetAlertService.showSuccess({
        title: "Deleted!",
        message: "Profile image deleted successfully.",
      });
      setPhotoPreview(null);
      setFormData((prev) => ({ ...prev, photo: null, photoUrl: null }));
      dispatch(getAdminProfile());
      dispatch(clearDeleteProfileImage());
    }
  }, [deleteData, dispatch]);

  // Profile image delete error
  useEffect(() => {
    if (deleteError) {
      SweetAlertService.showError({
        title: "Delete Failed",
        message: deleteError?.message || deleteError || "Failed to delete image.",
      });
      dispatch(clearDeleteProfileImage());
    }
  }, [deleteError, dispatch]);

  // Company logo upload success
  useEffect(() => {
    if (uploadCompanyLogoData) {
      SweetAlertService.showSuccess({
        title: "Uploaded!",
        message: "Company logo uploaded successfully.",
      });
      dispatch(getAdminProfile());
      dispatch(clearUploadCompanyLogo());
    }
  }, [uploadCompanyLogoData, dispatch]);

  // Company logo upload error
  useEffect(() => {
    if (uploadCompanyLogoError) {
      SweetAlertService.showError({
        title: "Upload Failed",
        message: uploadCompanyLogoError?.message || uploadCompanyLogoError || "Failed to upload company logo.",
      });
      setCompanyLogoPreview(formData.companyLogoUrl);
      dispatch(clearUploadCompanyLogo());
    }
    // eslint-disable-next-line
  }, [uploadCompanyLogoError, dispatch]);

  // Company logo delete success
  useEffect(() => {
    if (deleteCompanyLogoData) {
      SweetAlertService.showSuccess({
        title: "Deleted!",
        message: "Company logo deleted successfully.",
      });
      setCompanyLogoPreview(null);
      setFormData((prev) => ({ ...prev, companyLogo: null, companyLogoUrl: null }));
      dispatch(getAdminProfile());
      dispatch(clearDeleteCompanyLogo());
    }
  }, [deleteCompanyLogoData, dispatch]);

  // Company logo delete error
  useEffect(() => {
    if (deleteCompanyLogoError) {
      SweetAlertService.showError({
        title: "Delete Failed",
        message: deleteCompanyLogoError?.message || deleteCompanyLogoError || "Failed to delete company logo.",
      });
      dispatch(clearDeleteCompanyLogo());
    }
  }, [deleteCompanyLogoError, dispatch]);

  // ---------- CONFIRMATION MODAL HELPERS ----------
  const openConfirmationModal = (title, message, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      action: onConfirm,
    });
  };

  const handleConfirmationYes = async () => {
    if (confirmationModal.action) {
      await confirmationModal.action();
    }
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  const handleConfirmationNo = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  // ---------- FULL SCREEN PREVIEW HELPERS ----------
  const openImagePreview = (url, alt) => {
    if (!url) return;
    setImageModal({ isOpen: true, url, alt });
  };

  const closeImagePreview = () => {
    setImageModal({ isOpen: false, url: "", alt: "" });
  };

  // ---------- IMAGE VALIDATION HELPER ----------
  const validateImageFile = (file) => {
    if (!file) return { valid: false, error: "No file selected" };

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif"];

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: "Please upload a valid image file (JPG, JPEG, PNG, GIF)." };
    }

    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return { valid: false, error: "Please upload a valid image file (JPG, JPEG, PNG, GIF)." };
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: "Image size should not exceed 5MB." };
    }

    return { valid: true };
  };

  // ---------- HANDLERS ----------
  const handleImageSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      SweetAlertService.showError({
        title: validation.error.includes("5MB") ? "File Too Large" : "Invalid File",
        message: validation.error,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setPhotoPreview(URL.createObjectURL(file));
    setFormData((prev) => ({ ...prev, photo: file }));
    setImageError(false);
    dispatch(uploadProfileImage(file));
  };

  const handleCompanyLogoSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      SweetAlertService.showError({
        title: validation.error.includes("5MB") ? "File Too Large" : "Invalid File",
        message: validation.error,
      });
      if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
      return;
    }

    setCompanyLogoPreview(URL.createObjectURL(file));
    setFormData((prev) => ({ ...prev, companyLogo: file }));
    setCompanyLogoError(false);
    dispatch(uploadCompanyLogo(file));
  };

  const handleDeleteImage = () => {
    openConfirmationModal(
      "Delete Profile Image?",
      "Are you sure you want to delete your profile image?",
      () => {
        dispatch(deleteProfileImage());
      }
    );
  };

  const handleDeleteCompanyLogo = () => {
    openConfirmationModal(
      "Delete Company Logo?",
      "Are you sure you want to delete the company logo?",
      () => {
        dispatch(deleteCompanyLogo());
      }
    );
  };

  const handleInputChange = (e, field) => {
    const value = e?.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!formData.firstName.trim()) {
      SweetAlertService.showError({
        title: "Validation Error",
        message: "First name is required.",
      });
      return;
    }

    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      mobile_number: formData.phone,
    };

    if (!isCompanyDisabled) {
      payload.company_name = formData.companyName;
    }
    dispatch(updateAdminProfile(payload));
  };

  const handleCancel = () => {
    if (profile) {
      const imageUrl = sanitizeImageUrl(profile.profile_image);
      const logoUrl = sanitizeImageUrl(profile.company_logo);
      setFormData({
        firstName: profile.first_name || "",
        lastName: profile.last_name || "",
        email: profile.email || "",
        phone: profile.mobile_number || "",
        companyName: profile.company_name || "",
        photo: null,
        photoUrl: imageUrl,
        companyLogo: null,
        companyLogoUrl: logoUrl,
      });
      setPhotoPreview(imageUrl);
      setCompanyLogoPreview(logoUrl);
      setImageError(false);
      setCompanyLogoError(false);
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        companyName: "",
        photo: null,
        photoUrl: null,
        companyLogo: null,
        companyLogoUrl: null,
      });
      setPhotoPreview(null);
      setCompanyLogoPreview(null);
    }
  };

  const handleImageError = () => {
    console.error("Failed to load image:", photoPreview);
    setImageError(true);
  };

  const handleCompanyLogoError = () => {
    console.error("Failed to load company logo:", companyLogoPreview);
    setCompanyLogoError(true);
  };

  const getInitials = () => {
    const first = formData.firstName?.charAt(0)?.toUpperCase() || "";
    const last = formData.lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "?";
  };

  const getCompanyInitials = () => {
    const words = formData.companyName?.trim().split(" ") || [];
    if (words.length >= 2) {
      return (words[0]?.charAt(0)?.toUpperCase() || "") + (words[1]?.charAt(0)?.toUpperCase() || "");
    }
    return formData.companyName?.charAt(0)?.toUpperCase() || "C";
  };

  const isAnyLoading = loading || updateLoading || uploadLoading || deleteLoading || uploadCompanyLogoLoading || deleteCompanyLogoLoading;

  // ---------- RENDER ----------
  return (
    <React.Fragment>
      <div className="w-full bg-white">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="font-lato font-medium text-[16px] text-[#101828] mb-1 text-start">
              Personal info
            </h2>
            <p className="font-lato text-[14px] text-[#475467] text-start">
              Update your photo and personal details here.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg bg-[#FFF4E5] text-[#344054] text-sm font-medium hover:bg-orange-100 transition"
              disabled={isAnyLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-lg bg-[#FF8E1C] text-white text-sm font-medium hover:bg-[#e67e16] transition"
              disabled={isAnyLoading}
            >
              {updateLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6 border-t border-[#EAECF0] pt-6">
          
          {/* Row 1: Your Photo */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              Your photo
            </label>
            <div className="col-span-9 text-start">
              <div className="relative inline-block w-20 h-20">
                
                {/* Avatar Preview */}
                <div className="w-full h-full rounded-full bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-200">
                  {photoPreview && !imageError ? (
                    <img
                      src={photoPreview}
                      alt="Profile"
                      // ADDED: Cursor pointer and open preview onclick
                      className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition"
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      onError={handleImageError}
                      onClick={() => openImagePreview(photoPreview, "Profile Photo")}
                    />
                  ) : photoPreview && imageError ? (
                    <span className="text-xl font-semibold text-gray-500">
                      {getInitials()}
                    </span>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Upload Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition z-10"
                  disabled={uploadLoading}
                  title="Upload new photo"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 5.33333L8 2M8 2L4.66667 5.33333M8 2V10" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.gif"
                  onChange={handleImageSelect}
                  disabled={uploadLoading}
                />

                {/* Delete Button (Red Cross) */}
                {(formData.photoUrl || photoPreview) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage();
                    }}
                    className="absolute top-0 right-0 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-red-50 transition z-10"
                    style={{ transform: 'translate(20%, -20%)' }}
                    disabled={deleteLoading}
                    title="Delete photo"
                  >
                    <svg width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 1L1 13M1 1L13 13" stroke="#F04438" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Company Logo */}
          {canSeeCompanyLogo && (
            <div className="grid grid-cols-12 gap-6 items-center">
              <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
                Company logo
              </label>
              <div className="col-span-9 text-start">
                <div className="relative inline-block w-20 h-20">
                  
                  {/* Logo Preview */}
                  <div className="w-full h-full rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-200">
                    {companyLogoPreview && !companyLogoError ? (
                      <img
                        src={companyLogoPreview}
                        alt="Company Logo"
                        // ADDED: Cursor pointer and open preview onclick
                        className="w-full h-full object-contain p-1 cursor-pointer hover:opacity-80 transition"
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                        onError={handleCompanyLogoError}
                        onClick={() => openImagePreview(companyLogoPreview, "Company Logo")}
                      />
                    ) : companyLogoPreview && companyLogoError ? (
                      <span className="text-xl font-semibold text-gray-500">
                        {getCompanyInitials()}
                      </span>
                    ) : (
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 21V5C19 4.46957 18.7893 3.96086 18.4142 3.58579C18.0391 3.21071 17.5304 3 17 3H7C6.46957 3 5.96086 3.21071 5.58579 3.58579C5.21071 3.96086 5 4.46957 5 5V21M19 21H5M19 21H21M5 21H3M9 7H10M9 11H10M14 7H15M14 11H15M12 21V16C12 15.7348 11.8946 15.4804 11.7071 15.2929C11.5196 15.1054 11.2652 15 11 15H13C13.2652 15 13.5196 15.1054 13.7071 15.2929C13.8946 15.4804 14 15.7348 14 16V21" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>

                  {/* Actions for Company Admin Only */}
                  {canUploadCompanyLogo && (
                    <>
                      {/* Upload Button */}
                      <button
                        type="button"
                        onClick={() => companyLogoInputRef.current.click()}
                        className="absolute -bottom-2 -right-2 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition z-10"
                        disabled={uploadCompanyLogoLoading}
                        title="Upload new logo"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 5.33333L8 2M8 2L4.66667 5.33333M8 2V10" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>

                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={companyLogoInputRef}
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.gif"
                        onChange={handleCompanyLogoSelect}
                        disabled={uploadCompanyLogoLoading}
                      />

                      {/* Delete Button (Red Cross) */}
                      {(formData.companyLogoUrl || companyLogoPreview) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCompanyLogo();
                          }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-red-50 transition z-10"
                          disabled={deleteCompanyLogoLoading}
                          title="Delete logo"
                        >
                          <svg width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13M1 1L13 13" stroke="#F04438" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Row 3: First Name */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              First Name
            </label>
            <div className="col-span-9">
              <Input
                type="text"
                placeholder="Enter First Name here"
                value={formData.firstName}
                inputchangehandler={(e) => handleInputChange(e, "firstName")}
                className="w-full"
              />
            </div>
          </div>

          {/* Row 4: Last Name */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              Last Name
            </label>
            <div className="col-span-9">
              <Input
                type="text"
                placeholder="Enter Last Name here"
                value={formData.lastName}
                inputchangehandler={(e) => handleInputChange(e, "lastName")}
                className="w-full"
              />
            </div>
          </div>

          {/* Row 5: Email Address (read-only) */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              Email address
            </label>
            <div className="col-span-9 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.3333 4.99992C18.3333 4.07945 17.5871 3.33325 16.6666 3.33325H3.33329C2.41282 3.33325 1.66663 4.07945 1.66663 4.99992M18.3333 4.99992V14.9999C18.3333 15.9204 17.5871 16.6666 16.6666 16.6666H3.33329C2.41282 16.6666 1.66663 15.9204 1.66663 14.9999V4.99992M18.3333 4.99992L9.99996 11.6666L1.66663 4.99992" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="email"
                value={formData.email}
                disabled={true}
                readOnly
                className="w-full input pl-10 pr-3 py-2 input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-50"
              />
            </div>
          </div>

          {/* Row 6: Phone Number */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              Phone number
            </label>
            <div className="col-span-9 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.37614 6.08214C5.95614 7.29016 6.7468 8.42236 7.74811 9.42367C8.74943 10.425 9.88163 11.2156 11.0896 11.7956C11.1936 11.8455 11.2455 11.8705 11.3113 11.8896C11.5449 11.9577 11.8318 11.9088 12.0296 11.7671C12.0853 11.7273 12.1329 11.6797 12.2282 11.5844C12.5195 11.2931 12.6652 11.1474 12.8117 11.0521C13.3641 10.693 14.0762 10.693 14.6286 11.0521C14.7751 11.1474 14.9208 11.2931 15.2121 11.5844L15.3745 11.7468C15.8174 12.1897 16.0388 12.4111 16.1591 12.6489C16.3983 13.1219 16.3983 13.6804 16.1591 14.1534C16.0388 14.3912 15.8174 14.6126 15.3745 15.0555L15.2432 15.1869C14.8018 15.6282 14.5811 15.8489 14.2811 16.0174C13.9482 16.2045 13.4311 16.3389 13.0493 16.3378C12.7051 16.3368 12.4699 16.27 11.9996 16.1365C9.47175 15.419 7.08645 14.0653 5.09646 12.0753C3.10648 10.0853 1.75275 7.70004 1.03527 5.17221C0.901769 4.70184 0.835015 4.46665 0.833992 4.12253C0.832855 3.74068 0.967324 3.2236 1.15434 2.89068C1.32288 2.59065 1.54356 2.36998 1.98491 1.92862L2.11627 1.79726C2.55914 1.35439 2.78057 1.13296 3.01839 1.01267C3.49136 0.773446 4.04991 0.773446 4.52288 1.01267C4.7607 1.13296 4.98213 1.35439 5.425 1.79726L5.58739 1.95965C5.87873 2.25099 6.0244 2.39666 6.11964 2.54314C6.4788 3.09555 6.4788 3.8077 6.11964 4.36011C6.0244 4.50659 5.87873 4.65226 5.58739 4.9436C5.49213 5.03886 5.4445 5.08649 5.40464 5.14217C5.26296 5.34002 5.21404 5.62691 5.28214 5.86053C5.30131 5.92628 5.32625 5.97823 5.37614 6.08214Z" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Enter Phone number here"
                value={formData.phone}
                onChange={(e) => handleInputChange(e, "phone")}
                className="w-full input pl-10 pr-3 py-2 input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>

          {/* Row 7: Company Name */}
          <div className="grid grid-cols-12 gap-6 items-center">
            <label className="col-span-3 font-lato text-[14px] font-medium text-[#344054] text-start">
              Company Name
            </label>
            <div className="col-span-9">
              <Input
                type="text"
                placeholder="Enter Company Name here"
                value={formData.companyName}
                inputchangehandler={(e) => handleInputChange(e, "companyName")}
                className="w-full"
                disabled={isCompanyDisabled}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Full-Screen Image Preview Modal */}
      <ImagePreviewModal
        isOpen={imageModal.isOpen}
        onClose={closeImagePreview}
        imageUrl={imageModal.url}
        altText={imageModal.alt}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        dialogOpen={confirmationModal.isOpen}
        onDialogClose={handleConfirmationNo}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={handleConfirmationYes}
        showYesNo={true}
      />
    </React.Fragment>
  );
};

export default PersonalInfoForm;